import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { growthApi } from '@/services/growth.api';
import { Alert, Button, Spinner } from '@/components/ui';

/** Where this person stands on the church's own discipleship path — a
 *  single current step, not a history log (see `PersonGrowthProgress`'s own
 *  doc comment for why). */
export function GrowthTab({ personId }: { readonly personId: string }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);

  const progress = useQuery({
    queryKey: ['growth', 'progress', personId],
    queryFn: ({ signal }) => growthApi.getProgress(personId, signal),
  });
  const steps = useQuery({
    queryKey: ['growth', 'steps'],
    queryFn: () => growthApi.listSteps(),
    enabled: isEditing,
  });

  const setStep = useMutation({
    mutationFn: (stepId: string | null) => growthApi.setProgress(personId, { stepId }),
    onSuccess: () => {
      setIsEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['growth', 'progress', personId] });
    },
  });

  if (progress.isPending) return <Spinner label={t('common.states.loading')} />;
  if (progress.isError) return <Alert tone="danger">{t('errors.INTERNAL_ERROR')}</Alert>;

  if (isEditing) {
    return (
      <div className="growth-tab">
        {steps.isPending ? (
          <Spinner label={t('common.states.loading')} />
        ) : (
          <select
            className="field-input"
            aria-label={t('people.growth.step')}
            value={progress.data.stepId ?? ''}
            onChange={(event) => setStep.mutate(event.target.value || null)}
            disabled={setStep.isPending}
          >
            <option value="">{t('people.growth.notStarted')}</option>
            {(steps.data ?? []).map((step) => (
              <option key={step.id} value={step.id}>
                {step.name}
              </option>
            ))}
          </select>
        )}
        <Button variant="ghost" onClick={() => setIsEditing(false)}>
          {t('common.actions.cancel')}
        </Button>
      </div>
    );
  }

  return (
    <div className="growth-tab">
      <dl className="detail-grid">
        <div className="detail-row">
          <dt className="detail-label">{t('people.growth.step')}</dt>
          <dd className="detail-value">
            {progress.data.stepName ?? (
              <span className="cell-empty">{t('people.growth.notStarted')}</span>
            )}
          </dd>
        </div>
      </dl>

      {can('people.growthProgress.manage') && (
        <Button variant="secondary" onClick={() => setIsEditing(true)}>
          {t('common.actions.edit')}
        </Button>
      )}

      {can('people.growthStep.read') && (
        <p className="field-hint">
          <Link to="/people/growth/steps">{t('people.growth.manageSteps')}</Link>
        </p>
      )}
    </div>
  );
}
